'use client';

/**
 * API namespace `reviews` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L8190-L8281).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const reviews = {
  /**
   * POST /api/market/orders/:orderNumber/reviews — submit a
   * bidirectional review. The `direction` field determines who is
   * rating whom:
   *   - `buyer_to_seller`  → buyer rates seller (drives Seller.rating).
   *   - `seller_to_buyer`  → seller rates buyer (drives
   *                          User.market.buyerReputation — Q6).
   *
   * Pre-conditions (enforced in `review.service.submitReview` — Task B):
   *   - `order.status === 'completed'` (Q3 Founder).
   *   - NO existing review with same (orderNumber, direction) — 1 per
   *     direction per order (compound unique index — SPEC-084 §3.3).
   *   - `fromUserId === order.buyerId` (if direction='buyer_to_seller')
   *     OR `fromUserId === order.sellerId` (if direction='seller_to_buyer').
   *
   * Authz: `requireAuth` + `requireMarketReviewsEnabled`. NO 2FA gate
   * (reviews do not move credits — they are a reputational signal).
   *
   * Spec ref: SPEC-084 Fase 6 §5.1 (POST /reviews), §4.3 (submitReview);
   *           SPEC-NEGOCIO §10.10 (INV-MKT-18 bidirectional + 1-5 estrellas).
   */
  async submit(
    orderNumber: string,
    body: { direction: ReviewDirection; rating: ReviewRating; comment?: string; images?: string[]; videoUrl?: string | null },
    signal?: AbortSignal,
  ): Promise<ReviewResponse> {
    const payload: Record<string, unknown> = {
      direction: body.direction,
      rating: body.rating,
      comment: body.comment ?? '',
    };
    if (body.images && body.images.length > 0) payload.images = body.images;
    if (body.videoUrl) payload.videoUrl = body.videoUrl;
    return apiFetch<ReviewResponse>(
      `/market/orders/${encodeURIComponent(orderNumber)}/reviews`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        signal,
      },
    );
  },
  /**
   * GET /api/market/sellers/:sellerId/reviews — S2-C2 public reviews-list
   * endpoint. Returns a paginated, privacy-filtered list of the seller's
   * `buyer_to_seller` reviews (admin-hidden `moderated=true` excluded).
   * Powers the `<ReviewsList />` component on the public store page.
   *
   * Response shape: `ReviewListResponse` from `@profileia/shared` —
   * `reviews: ReviewListItem[]` + `total` + `page` + `limit` + `rating`.
   * Each `ReviewListItem` is privacy-filtered (NO `fromUserId`,
   * `orderNumber`, `disputeId`). `fromUserName` is "firstName + lastInitial."
   *
   * Authz: PUBLIC (no `requireAuth`); `requireMarketReviewsEnabled` FF gate.
   *
   * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item C-2 (CRITICAL).
   */
  async listBySeller(
    sellerId: string,
    page: number = 1,
    signal?: AbortSignal,
  ): Promise<ReviewListResponse> {
    const qs = new URLSearchParams({
      page: String(Math.max(1, page)),
      limit: '10',
    });
    return apiFetch<ReviewListResponse>(
      `/market/sellers/${encodeURIComponent(sellerId)}/reviews?${qs.toString()}`,
      { method: 'GET', signal },
    );
  },
};
